import { useEffect, useRef, useState } from 'react';
import { useSetAtom } from 'jotai';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { useTranslation } from 'next-i18next';

import { useSocialAuthService } from '@restworld/data-services';
import { useAuthContext } from '@restworld/feature-authentication';
import { showToast } from '@restworld/ui-ds-base';

import { AUTH_TOAST_CONTAINER_ID, openSocialUserTypeModal, setSocialSignupDetails } from '../../context/auth-flow';

export type GoogleButtonProps = {
  isModal?: boolean;
  closeModal?: () => void;
  // The account the visitor was routed here to reach, e.g. the `?email=` the
  // "email already used" 409 redirect carries. Google sign-in is login-only, so
  // when it comes back as a different account the button must not sidetrack into
  // signup. See the conflict-mismatch guardrail in onSuccess.
  expectedEmail?: string;
  // Fired when the guardrail trips (a Google account other than `expectedEmail`),
  // and with null on every fresh attempt. The parent owns where the resulting
  // notice is shown, so it can place it below the whole auth button group rather
  // than wedged next to this button.
  onConflictMismatch?: (mismatch: { expected: string; google: string } | null) => void;
};

// GIS caps the rendered button at 400px; we size it to the container so it lines
// up full-width with the other auth buttons instead of sitting narrow.
const GIS_MAX_WIDTH = 400;
const GIS_MIN_WIDTH = 200;

export function GoogleButton({ isModal, closeModal, expectedEmail, onConflictMismatch }: GoogleButtonProps) {
  const authContext = useAuthContext();
  const socialAuthService = useSocialAuthService();
  const setSocialDetails = useSetAtom(setSocialSignupDetails);
  const openUserTypeModal = useSetAtom(openSocialUserTypeModal);
  const { t, i18n } = useTranslation('auth-flow');

  const containerRef = useRef<HTMLDivElement>(null);
  const [buttonWidth, setButtonWidth] = useState<number>(GIS_MAX_WIDTH);

  // Track the container width so Google's button fills it (responsive).
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const measure = () => setButtonWidth(Math.min(GIS_MAX_WIDTH, Math.max(GIS_MIN_WIDTH, Math.round(el.offsetWidth))));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  function dismissAuthModal() {
    if (isModal && closeModal) closeModal();
  }

  function onSuccess(credentialResponse: CredentialResponse) {
    // GIS returns the ID token in `credential`.
    const idToken = credentialResponse.credential;
    if (!idToken) return;

    socialAuthService
      .loginGoogle({ id_token: idToken })
      .then((res) => {
        const data = res?.data;
        // Fresh attempt: drop any mismatch notice from a previous click.
        onConflictMismatch?.(null);

        if (data?.registered) {
          // Existing user: log them in (unchanged behavior).
          if (data.jwt && authContext?.verifyToken) authContext.verifyToken(data.jwt);
          dismissAuthModal();
          // Timed success banner, same helper used across the app (e.g. phone OTP verify).
          showToast(t('google_login_success'), 'success', { containerId: AUTH_TOAST_CONTAINER_ID, autoClose: 4000 });
          return;
        }

        const googleEmail = data?.email ?? null;

        // Conflict-login guardrail. The visitor was routed here to reach an
        // existing account (`expectedEmail`, e.g. after an "email already used"
        // 409), but signed in with a different Google account. Sending them into
        // worker/employer signup would create a second account under the Google
        // email and never reach the account they were told about, so stop and
        // point them back: email login to `expectedEmail`, or the Google account
        // for it. Nothing is stashed and no signup modal opens.
        if (expectedEmail && googleEmail && googleEmail.toLowerCase() !== expectedEmail.toLowerCase()) {
          onConflictMismatch?.({ expected: expectedEmail, google: googleEmail });
          return;
        }

        // New user: stash the verified Google profile and ask worker vs employer.
        setSocialDetails({
          email: googleEmail,
          name: data?.name ?? null,
          google_id: data?.google_id ?? null,
          // Keep the ID token so the worker CV-upload can hand it to the backend for
          // server-side re-verification of the Google identity before account creation.
          id_token: idToken,
        });
        dismissAuthModal();
        openUserTypeModal();
      })
      .catch(() => {
        // Backend/network failure (or a rejected token, e.g. unverified email).
        showToast(t('google_login_error'), 'error', { containerId: AUTH_TOAST_CONTAINER_ID, autoClose: 5000 });
      });
  }

  return (
    <div ref={containerRef} className="flex w-full justify-center">
      <GoogleLogin
        onSuccess={onSuccess}
        onError={() => {
          // Google itself failed/aborted the sign-in before returning a token.
          showToast(t('google_login_error'), 'error', { containerId: AUTH_TOAST_CONTAINER_ID, autoClose: 5000 });
        }}
        theme="outline"
        text="continue_with"
        shape="rectangular"
        size="large"
        logo_alignment="center"
        locale={i18n.language}
        width={buttonWidth.toString()}
      />
    </div>
  );
}
